import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

const faqs = [
  {
    q: "How do manufacturers join a bidding process?",
    a: "Once you launch a bidding request, DeMaxtore invites verified Turkish manufacturers to participate online. Manufacturers join anonymously and submit competitive factory offers during the live bidding window inside the Workspace.",
  },
  {
    q: "Can I invite my existing suppliers?",
    a: "Yes. You can invite your existing Turkish manufacturer network into a CommodityBid™️ bidding process. They are onboarded through the same verification process and compete under the same anonymous participation conditions.",
  },
  {
    q: "Who organizes the bidding process?",
    a: "You create the bidding request with your requirements. DeMaxtore organizes the online bidding process — inviting verified manufacturers, managing anonymous participation and keeping the process structured from start to award.",
  },
  {
    q: "Do manufacturers submit offers directly?",
    a: "Yes. Manufacturers submit competitive factory offers directly through CommodityBid™️ during the live bidding process. DeMaxtore does not insert pricing in the middle — you receive every factory offer exactly as submitted.",
  },
  {
    q: "Can I award any participating manufacturer?",
    a: "Yes. You review all submitted factory offers and award the winning manufacturer based on your procurement criteria. Any participating manufacturer who meets your requirements can be selected.",
  },
  {
    q: "How do I continue after awarding the winner?",
    a: "Once you award the winning manufacturer, a Purchase Order is created inside the Workspace. From there, the process continues seamlessly into FreightIQ™️, Inspection™️ and Live Shipment Tracking™️ — all in one place.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section
      data-testid="faq-section"
      id="faq"
      className="relative py-24 md:py-32 bg-white border-t border-slate-200/60"
    >
      <div className="max-w-[1000px] mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl"
        >
          <p className="font-inter text-[11px] font-bold tracking-[0.22em] uppercase text-[#3B82F6]">
            Frequently Asked
          </p>
          <h2 className="mt-3 font-display text-3xl md:text-[42px] font-extrabold tracking-tight text-[#0B1F3A] leading-[1.1]">
            Questions buyers ask.
          </h2>
        </motion.div>

        <div className="mt-12 rounded-[20px] bg-[#F8FAFC] border border-slate-200/80 cb-soft-shadow divide-y divide-slate-100 overflow-hidden">
          {faqs.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={item.q} data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-trigger-${i}`}
                  className="w-full flex items-center justify-between text-left px-6 md:px-8 py-5 md:py-6 hover:bg-white/70 transition-colors"
                  aria-expanded={isOpen}
                >
                  <span className="font-display font-semibold text-[15px] md:text-[16px] text-[#0B1F3A] pr-4">
                    {item.q}
                  </span>
                  <span
                    className={`shrink-0 h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${
                      isOpen ? "bg-[#0B1F3A] text-white" : "bg-white text-[#0B1F3A] border border-slate-200"
                    }`}
                  >
                    {isOpen ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28, ease: [0.25, 0.1, 0.25, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 md:px-8 pb-6 text-[14px] md:text-[15px] text-[#475569] leading-relaxed max-w-3xl">
                        {item.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
